/**
 * PLAN-WECHSEL-DIALOG
 *
 * Modal-Dialog für Upgrade/Downgrade zwischen FCMS-Plänen.
 * Zeigt Kostenvorschau, Limit-Warnungen und führt den Wechsel durch.
 */

import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, AlertTriangle, Loader2 } from 'lucide-react';
import { TWModal, TWModalFooter, TWModalButton } from '../ui/TWModal';
import {
  previewPlanChange,
  executePlanChange,
  formatSubscriptionPrice,
  type PlanChangePreview,
  type PlanChangeResult
} from '../../services/payment/subscriptionService';

interface PlanChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetPlanSlug: string;
  onSuccess: () => void;
  onDowngradeNeedsSelection?: (effectiveDate: string) => void;
}

export const PlanChangeDialog: React.FC<PlanChangeDialogProps> = ({
  isOpen,
  onClose,
  targetPlanSlug,
  onSuccess,
  onDowngradeNeedsSelection,
}) => {
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [result, setResult] = useState<PlanChangeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Preview laden wenn Dialog öffnet
  useEffect(() => {
    if (isOpen && targetPlanSlug) {
      loadPreview();
    } else {
      // Reset bei Schließen
      setPreview(null);
      setResult(null);
      setError(null);
      setLoading(true);
    }
  }, [isOpen, targetPlanSlug]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await previewPlanChange(targetPlanSlug);
      setPreview(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!preview) return;
    setExecuting(true);
    setError(null);
    try {
      const changeResult = await executePlanChange(targetPlanSlug);
      setResult(changeResult);

      // Downgrade mit Limit-Überschreitung: Ressourcen-Auswahl öffnen
      if (
        (changeResult.changeType === 'downgrade' || changeResult.changeType === 'downgrade_free') &&
        preview?.limitWarnings?.length &&
        onDowngradeNeedsSelection
      ) {
        onDowngradeNeedsSelection(changeResult.effectiveDate);
        return;
      }

      // Erfolg: Parent informieren
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Plan-Wechsel');
    } finally {
      setExecuting(false);
    }
  };

  const isUpgrade = preview?.changeType === 'upgrade';
  const isDowngrade = preview?.changeType === 'downgrade' || preview?.changeType === 'downgrade_free';

  const getTitle = () => {
    if (result) return 'Plan-Wechsel erfolgreich';
    if (!preview) return 'Plan-Wechsel';
    return isUpgrade ? 'Upgrade bestätigen' : 'Downgrade bestätigen';
  };

  const getIcon = () => {
    if (isUpgrade) return <ArrowUp className="w-5 h-5 text-status-success" />;
    return <ArrowDown className="w-5 h-5 text-orange-500" />;
  };

  return (
    <TWModal
      isOpen={isOpen}
      onClose={result ? () => { onClose(); } : onClose}
      title={getTitle()}
      icon={!loading && !error ? getIcon() : undefined}
      size="md"
    >
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Erfolg */}
      {result && !error && (
        <div className="space-y-4">
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">
              {result.message}
            </p>
          </div>
          <TWModalFooter>
            <TWModalButton variant="primary" onClick={onClose}>
              Verstanden
            </TWModalButton>
          </TWModalFooter>
        </div>
      )}

      {/* Preview */}
      {preview && !result && !loading && !error && (() => {
        // Intervall aus dem Backend-Preview ableiten (Yearly oder Monthly).
        // Suffix bestimmt, ob Preise als "/Monat" oder "/Jahr" angezeigt werden.
        const intervalSuffix = preview.interval === 'yearly' ? '/Jahr' : '/Monat';
        const periodLabel = preview.interval === 'yearly' ? 'Jahres' : 'Monats';
        return (
        <div className="space-y-5">
          {/* Von -> Nach */}
          <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="flex-1 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Aktuell</p>
              <p className="font-semibold text-gray-900 dark:text-white">{preview.currentPlan.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formatSubscriptionPrice(preview.currentPlan.price)}{intervalSuffix}</p>
            </div>
            <div className="shrink-0">
              {isUpgrade
                ? <ArrowUp className="w-5 h-5 text-status-success" />
                : <ArrowDown className="w-5 h-5 text-orange-500" />
              }
            </div>
            <div className="flex-1 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Neu</p>
              <p className="font-semibold text-gray-900 dark:text-white">{preview.newPlan.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formatSubscriptionPrice(preview.newPlan.price)}{intervalSuffix}</p>
            </div>
          </div>

          {/* Kosten-Info */}
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            {/* Stripe Upgrade */}
            {preview.paymentProvider === 'stripe' && isUpgrade && preview.proratedAmount != null && (
              <p>
                Sofortige Nachzahlung: <strong>{formatSubscriptionPrice(preview.proratedAmount)}</strong>.
                Ab nächster {periodLabel}-Periode: {formatSubscriptionPrice(preview.newPlan.price)}{intervalSuffix}.
              </p>
            )}

            {/* Stripe Downgrade */}
            {isDowngrade && (
              <p>
                Dein {preview.currentPlan.name}-Zugang bleibt bis zum{' '}
                <strong>{new Date(preview.effectiveDate).toLocaleDateString('de-DE')}</strong> bestehen.
                {preview.changeType !== 'downgrade_free' && (
                  <> Danach: {formatSubscriptionPrice(preview.newPlan.price)}{intervalSuffix}.</>
                )}
                {preview.changeType === 'downgrade_free' && (
                  <> Danach: Kostenloser Basic-Plan.</>
                )}
              </p>
            )}

          </div>

          {/* Limit-Warnungen */}
          {preview.limitWarnings && preview.limitWarnings.length > 0 && (
            <div className="rounded-lg border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/10 p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                  Du nutzt mehr Ressourcen als der neue Plan erlaubt
                </p>
              </div>
              <div className="space-y-1.5">
                {preview.limitWarnings.map((warning) => (
                  <div key={warning.resource} className="flex items-center justify-between text-xs text-orange-700 dark:text-orange-400">
                    <span>{warning.resource}</span>
                    <span className="font-medium">{warning.current} / {warning.newLimit}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-orange-600 dark:text-orange-400/80">
                Nach Bestätigung kannst du auswählen, welche Ressourcen du behalten möchtest.
              </p>
            </div>
          )}

          {/* Footer */}
          <TWModalFooter>
            <TWModalButton variant="cancel" onClick={onClose}>
              Abbrechen
            </TWModalButton>
            <TWModalButton
              variant={isUpgrade ? 'primary' : 'danger'}
              onClick={handleExecute}
              loading={executing}
            >
              {isUpgrade ? 'Jetzt upgraden' : 'Downgrade bestätigen'}
            </TWModalButton>
          </TWModalFooter>
        </div>
        );
      })()}
    </TWModal>
  );
};

export default PlanChangeDialog;
